import { useCallback, useEffect, useState } from "react";
import { serviceTenderWatch } from "../../services/service-tender-watch";

/** A bevakning as it stands on the server, for comparing the filter on screen against. */
export type SavedTenderWatch = {
  areaIds: string[];
  places: string[];
  cpvCodes: string[];
};

/**
 * Category and region are the same vocabulary on both marketplace tabs, so one filter serves
 * both and following the user across tabs costs nothing. CPV belongs to the public tab alone
 * and is handed over as a starting point rather than kept here.
 */
export type SharedTenderFilter = {
  areaIds: string[];
  setAreaIds: (areaIds: string[]) => void;
  places: string[];
  setPlaces: (places: string[]) => void;
  /** The saved bevakning's hand-picked codes, for the public tab to seed its control with. */
  initialCpvCodes: string[];
  /** The bevakning as last saved, or null when the user has none of their own. */
  saved: SavedTenderWatch | null;
  markSaved: (next: SavedTenderWatch) => void;
  /** False until the prefill has arrived. The public tab waits for it before seeding CPV. */
  ready: boolean;
  /**
   * Whether there is a signed-in user to save a bevakning for. The anonymous embed has none,
   * and a save there answers 401, which signs the visitor out of a page they never signed in
   * to - so the control that would send it must not be offered.
   */
  enabled: boolean;
};

const sameSet = (a: string[], b: string[]) => (
  a.length === b.length && [...a].sort().join("\u0000") === [...b].sort().join("\u0000")
);

/** Order is not meaningful in any of these, so only membership is compared. */
export const isUnchanged = (
  current: { areaIds: string[], places: string[], cpvCodes?: string[] },
  saved: SavedTenderWatch | null,
) => {
  if (!saved) return false;
  if (!sameSet(current.areaIds, saved.areaIds)) return false;
  if (!sameSet(current.places, saved.places)) return false;
  // A tab with no CPV control cannot change them, so it does not get a vote on whether the
  // bevakning has changed.
  if (current.cpvCodes && !sameSet(current.cpvCodes, saved.cpvCodes)) return false;
  return true;
};

export const useSharedTenderFilter = (enabled: boolean): SharedTenderFilter => {
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [places, setPlaces] = useState<string[]>([]);
  const [initialCpvCodes, setInitialCpvCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState<SavedTenderWatch | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return;
    }
    (async () => {
      try {
        const filter = await serviceTenderWatch.get();
        if (filter) {
          const nextAreaIds = (filter.areaIds ?? []).map(String);
          const nextPlaces = filter.places ?? [];
          const nextCpvCodes = filter.cpvCodes ?? [];
          setAreaIds(nextAreaIds);
          setPlaces(nextPlaces);
          setInitialCpvCodes(nextCpvCodes);
          // Only a bevakning of the user's own counts as saved; the company presentation is a
          // starting point, so saving it unchanged is a real action rather than a no-op.
          if (filter.isSaved) {
            setSaved({ areaIds: nextAreaIds, places: nextPlaces, cpvCodes: nextCpvCodes });
          }
        }
      } catch (error) {
        // An unreachable bevakning is not worth blocking the marketplace for; the tables
        // simply open unfiltered, which is what they did before this existed.
      } finally {
        setReady(true);
      }
    })();
  }, [enabled]);

  const markSaved = useCallback((next: SavedTenderWatch) => setSaved(next), []);

  return { areaIds, setAreaIds, places, setPlaces, initialCpvCodes, saved, markSaved, ready, enabled };
};
